import React from "react";
import { cn } from "@gettactic/helpers";
import { useAskTessa } from "@v2/components/ai/AskTessaProvider";
import { MaximizeIcon } from "lucide-react";
import { AiIcon } from "@v2/icons";
import {
  findFixedContainingBlock,
  findScrollContainer
} from "@webV2/pages/Tessa/helpers";

type DockSide = "left" | "right";

export const ChatBotToolCard: React.FC<
  React.PropsWithChildren<{
    className?: string;
    isThinking?: boolean;
    dock?: DockSide;
    transitionMs?: number;
    isZoomEnabled?: boolean;
    placeholderWidth?: number;
    placeholderHeight?: number;
    toolId?: string;
  }>
> = ({
  children,
  className,
  isThinking,
  dock = "right",
  transitionMs = 380,
  isZoomEnabled = true,
  placeholderWidth = 220,
  placeholderHeight = 40,
  toolId
}) => {
  const askTessa = useAskTessa();
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  const [isFull, setIsFull] = React.useState(false);
  const animatingRef = React.useRef(false);

  const restoreStyleRef = React.useRef<Partial<CSSStyleDeclaration> | null>(
    null
  );
  const originalRectRef = React.useRef<DOMRect | null>(null); // NEW: remember original size

  const placeholderRef = React.useRef<HTMLDivElement | null>(null);
  const placeholderCleanupRef = React.useRef<(() => void) | null>(null);

  const originalParentRef = React.useRef<HTMLElement | null>(null);
  const originalNextSiblingRef = React.useRef<Node | null>(null);
  const hoistedRef = React.useRef<boolean>(false);

  const exitFullRef = React.useRef<() => void>(() => {});

  const play = (
    el: HTMLElement,
    frame: {
      top: number;
      left: number;
      width: number;
      height: number;
      radius: number;
    }
  ) => {
    el.style.transition = `top ${transitionMs}ms cubic-bezier(.2,.7,0,1),
                           left ${transitionMs}ms cubic-bezier(.2,.7,0,1),
                           width ${transitionMs}ms cubic-bezier(.2,.7,0,1),
                           height ${transitionMs}ms cubic-bezier(.2,.7,0,1),
                           border-radius ${transitionMs}ms ease`;
    requestAnimationFrame(() => {
      el.style.top = `${frame.top}px`;
      el.style.left = `${frame.left}px`;
      el.style.width = `${frame.width}px`;
      el.style.height = `${frame.height}px`;
      el.style.borderRadius = `${frame.radius}px`;
    });
  };

  // Compact, interactive placeholder (fixed small size)
  const makeInteractivePlaceholder = () => {
    const ph = document.createElement("div");
    ph.setAttribute("role", "button");
    ph.setAttribute("tabindex", "0");
    ph.setAttribute("aria-label", "Return the card here");
    ph.style.width = `${placeholderWidth}px`;
    ph.style.height = `${placeholderHeight}px`;
    ph.style.display = "flex";
    ph.style.alignItems = "center";
    ph.style.justifyContent = "center";
    ph.style.gap = "8px";
    ph.style.boxSizing = "border-box";
    ph.style.border = "1px dashed rgba(0,0,0,0.15)";
    ph.style.borderRadius = "12px";
    ph.style.background = "white";
    ph.style.cursor = "pointer";
    ph.style.userSelect = "none";
    ph.style.color = "rgba(0,0,0,0.55)";
    ph.style.fontSize = "12px";
    ph.style.fontWeight = "500";
    ph.style.alignSelf = "flex-start"; // don't stretch in flex parents

    ph.innerHTML = `<span style="padding:4px 8px;border-radius:9999px;border:1px solid rgba(0,0,0,.12)">Close Tool</span>`;

    const onClick = () => exitFullRef.current();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        exitFullRef.current();
      }
    };
    ph.addEventListener("click", onClick);
    ph.addEventListener("keydown", onKey);
    placeholderCleanupRef.current = () => {
      ph.removeEventListener("click", onClick);
      ph.removeEventListener("keydown", onKey);
    };
    return ph;
  };

  const enterFull = React.useCallback(() => {
    if (animatingRef.current) return;
    const el = containerRef.current;
    if (!el) return;

    animatingRef.current = true;
    setIsFull(true);

    askTessa?.fullPageToolDisclosure.open({ partId: toolId || "tool" });

    const rect = el.getBoundingClientRect();
    originalRectRef.current = rect;

    const ph = makeInteractivePlaceholder();
    placeholderRef.current = ph;
    el.parentElement?.insertBefore(ph, el);

    restoreStyleRef.current = {
      position: el.style.position,
      top: el.style.top,
      left: el.style.left,
      width: el.style.width,
      height: el.style.height,
      right: el.style.right,
      bottom: el.style.bottom,
      margin: el.style.margin,
      zIndex: el.style.zIndex,
      borderRadius: el.style.borderRadius
    };

    const hasFixedBlock = !!findFixedContainingBlock(el);
    const hasScrollContainer = !!findScrollContainer(el);
    const shouldHoist = hasFixedBlock || hasScrollContainer;

    originalParentRef.current = el.parentElement as HTMLElement | null;
    originalNextSiblingRef.current = el.nextSibling;

    const startTop = rect.top + window.scrollY;
    const startLeft = rect.left + window.scrollX;

    if (shouldHoist) {
      document.body.appendChild(el);
      hoistedRef.current = true;
    }
    el.style.position = "fixed";
    el.style.top = `${startTop}px`;
    el.style.left = `${startLeft}px`;
    el.style.width = `${rect.width}px`;
    el.style.height = `${rect.height}px`;
    el.style.margin = "0";
    el.style.zIndex = "9999";
    el.style.willChange = "top,left,width,height,border-radius";

    const targetW = Math.round(window.innerWidth - 500);
    const targetH = window.innerHeight;
    const targetLeft = dock === "right" ? window.innerWidth - targetW : 0;
    const targetTop = 0;

    play(el, {
      top: targetTop,
      left: targetLeft,
      width: targetW,
      height: targetH,
      radius: 16
    });

    window.setTimeout(() => {
      el.style.transition = "";
      el.style.willChange = "";
      animatingRef.current = false;
    }, transitionMs + 30);
  }, [dock, transitionMs, askTessa, placeholderWidth, placeholderHeight]);

  const exitFull = React.useCallback(() => {
    if (animatingRef.current) return;
    const el = containerRef.current;
    const ph = placeholderRef.current;
    const restore = restoreStyleRef.current;
    const original = originalRectRef.current;
    if (!el || !ph || !restore) {
      setIsFull(false);
      return;
    }
    animatingRef.current = true;

    askTessa?.fullPageToolDisclosure.close();

    const phRect = ph.getBoundingClientRect();
    const targetTop = phRect.top + window.scrollY;
    const targetLeft = phRect.left + window.scrollX;
    const targetWidth = original?.width ?? phRect.width;
    const targetHeight = original?.height ?? phRect.height;

    el.style.transition = `top ${transitionMs}ms cubic-bezier(.2,.7,0,1),
                           left ${transitionMs}ms cubic-bezier(.2,.7,0,1),
                           width ${transitionMs}ms cubic-bezier(.2,.7,0,1),
                           height ${transitionMs}ms cubic-bezier(.2,.7,0,1),
                           border-radius ${transitionMs}ms ease`;
    requestAnimationFrame(() => {
      el.style.top = `${targetTop}px`;
      el.style.left = `${targetLeft}px`;
      el.style.width = `${targetWidth}px`;
      el.style.height = `${targetHeight}px`;
      el.style.borderRadius = "12px";
    });

    window.setTimeout(() => {
      // Reinsert element where it was
      if (hoistedRef.current && originalParentRef.current) {
        if (originalNextSiblingRef.current) {
          originalParentRef.current.insertBefore(
            el,
            originalNextSiblingRef.current
          );
        } else {
          originalParentRef.current.appendChild(el);
        }
      } else {
        ph.parentElement?.insertBefore(el, ph);
      }

      // Restore inline styles
      el.style.transition = "";
      el.style.position = restore.position ?? "";
      el.style.top = restore.top ?? "";
      el.style.left = restore.left ?? "";
      el.style.width = restore.width ?? "";
      el.style.height = restore.height ?? "";
      el.style.right = restore.right ?? "";
      el.style.bottom = restore.bottom ?? "";
      el.style.margin = restore.margin ?? "";
      el.style.zIndex = restore.zIndex ?? "";
      el.style.borderRadius = restore.borderRadius ?? "";

      // Cleanup
      placeholderCleanupRef.current?.();
      ph.remove();
      placeholderRef.current = null;
      placeholderCleanupRef.current = null;

      hoistedRef.current = false;
      originalParentRef.current = null;
      originalNextSiblingRef.current = null;
      restoreStyleRef.current = null;
      originalRectRef.current = null;

      animatingRef.current = false;
      setIsFull(false);
    }, transitionMs + 30);
  }, [transitionMs, askTessa]);

  exitFullRef.current = exitFull;

  const toggle = React.useCallback(() => {
    if (!isZoomEnabled) return;
    if (isFull) exitFull();
    else enterFull();
  }, [isZoomEnabled, isFull, enterFull, exitFull]);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isFull) {
        e.preventDefault();
        exitFull();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isFull, exitFull]);

  if (isThinking) return "Still thinking...";
  if (askTessa?.sidebarDisclosure?.isOpen)
    return <div className="mt-2 max-w-[800px]">{children}</div>;

  const isCompact = askTessa?.fullPageToolDisclosure?.isOpen && !isFull;
  const isPopup = !!askTessa?.isPopup;

  return (
    <div
      className={cn(
        "my-4 overflow-hidden rounded-md bg-white p-4 transition-all",
        isCompact || isPopup ? "" : "border border-neutral-200 shadow-2xl",
        className,
        !askTessa?.fullPageToolDisclosure?.isOpen && ""
      )}
      ref={containerRef}
    >
      {isZoomEnabled && (
        <div className="mb-2 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={toggle}
            className="inline-flex items-center rounded-md border border-neutral-200 px-2 py-1 text-xs hover:bg-neutral-50 active:scale-[0.98]"
            aria-label={isFull ? "Exit expanded view" : "Expand"}
          >
            <MaximizeIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className={"flex gap-2"}>
        {(isCompact || isPopup) && (
          <div className={"w-4 pt-5"}>
            <AiIcon className={"size-4"} />
          </div>
        )}
        <div className={"flex-1"}>{children}</div>
      </div>
    </div>
  );
};
